import { sessionBarsToNative, timelineBars } from '@/audio/clipTiming';
/**
 * autoArranger.js — Section 8
 *
 * Pre-built song structure templates that distribute loops
 * across a full arrangement. Teaching tool disguised as a feature.
 */

import { snapToGrid } from '@/state/useSnapGrid';
import { INSTRUMENT_COLORS, BARS_RANGE } from '@/utils/constants';

// =====================================================================
// 8.1 Templates — exact structures from spec
// =====================================================================

// Section density mapping by type
const DENSITY = {
  // Sparse: 0.2-0.3 (intros, fades, ambients, outs)
  intro: 0.25, outro: 0.25, fade: 0.2, ambient: 0.2, out: 0.25, open: 0.3,
  in: 0.25,
  // Medium: 0.5-0.7 (verses, flows, builds, pre-choruses, switches, drifts, breaks, resolves)
  verse: 0.6, flow: 0.6, build: 0.5, pre: 0.5, switch: 0.5,
  drift: 0.4, break_: 0.4, resolve: 0.5, groove: 0.65,
  // Full: 0.9-1.0 (hooks, drops, choruses, peaks, mains)
  hook: 1.0, drop: 1.0, chorus: 1.0, peak: 1.0, main: 0.9,
  // Bridge: special (no drums, melody/texture only)
  bridge: 0.5,
};

function d(type) { return DENSITY[type] || 0.5; }

// Batch 3.5: bar-1 guarantee. Fixed ranking, unrecognized/missing groups rank last.
const INSTRUMENT_RANK = { drums: 0, bass: 1, melody: 2, texture: 3, fx: 4 };
function rankOf(instrument) { return INSTRUMENT_RANK[instrument] ?? 5; }

/**
 * Deterministically pick up to 2 tracks to force a real clip into the first
 * section, so an arrangement can never roll an empty/silent intro. Only
 * tracks that already have a loop assigned (clips.length > 0) are eligible.
 * A second track is only forced if it represents a second, distinct
 * instrument group, not just a second track of the same group.
 */
function pickForcedIntroTracks(tracks) {
  const eligible = tracks.filter((t) => t.clips.length > 0);
  if (eligible.length === 0) return [];
  const sorted = [...eligible].sort((a, b) => rankOf(a.instrument) - rankOf(b.instrument));
  const first = sorted[0];
  const second = sorted.find((t) => rankOf(t.instrument) !== rankOf(first.instrument));
  return second ? [first, second] : [first];
}

export const TEMPLATES = [
  {
    id: 'classic-boom-bap',
    name: 'Classic Boom Bap',
    totalBars: 60,
    teachingConcept: 'Traditional hip-hop song form',
    tooltip: 'Intros build anticipation by starting with fewer elements. Try removing drums from your intro.',
    sections: [
      { name: 'Intro',  bars: 4,  density: d('intro') },
      { name: 'Verse',  bars: 16, density: d('verse') },
      { name: 'Hook',   bars: 8,  density: d('hook') },
      { name: 'Verse',  bars: 16, density: d('verse') },
      { name: 'Hook',   bars: 8,  density: d('hook') },
      { name: 'Outro',  bars: 8,  density: d('outro') },
    ],
  },
  {
    id: 'trap-wave',
    name: 'Trap Wave',
    totalBars: 48,
    teachingConcept: 'Energy dynamics and tension/release',
    tooltip: 'Drops hit hardest after a quiet break. Build tension with fewer tracks, then unleash everything.',
    sections: [
      { name: 'Intro', bars: 4,  density: d('build') },
      { name: 'Hook',  bars: 12, density: d('drop') },
      { name: 'Verse', bars: 8,  density: d('break_') },
      { name: 'Hook',  bars: 12, density: d('drop') },
      { name: 'Verse', bars: 8,  density: d('break_') },
      { name: 'Outro', bars: 4,  density: d('fade') },
    ],
  },
  {
    id: 'lofi-chill',
    name: 'Lo-Fi Chill',
    totalBars: 48,
    teachingConcept: 'Atmosphere and mood consistency',
    tooltip: 'Lo-fi keeps a steady vibe. Subtle changes in texture keep it interesting without breaking the mood.',
    sections: [
      { name: 'Intro',   bars: 4,  density: d('ambient') },
      { name: 'Verse',   bars: 16, density: d('flow') },
      { name: 'Bridge',  bars: 4,  density: d('drift'), noDrums: true },
      { name: 'Verse',   bars: 16, density: d('flow') },
      { name: 'Outro',   bars: 8,  density: d('resolve') },
    ],
  },
  {
    id: 'quick-flip',
    name: 'Quick Flip',
    totalBars: 40,
    teachingConcept: 'Minimal arrangement, maximum impact',
    tooltip: 'Quick flips prove you don\'t need length to make an impact. Every bar counts.',
    sections: [
      { name: 'Intro',  bars: 4, density: d('in') },
      { name: 'Hook',   bars: 8, density: d('main') },
      { name: 'Verse',  bars: 4, density: d('switch') },
      { name: 'Hook',   bars: 8, density: d('main') },
      { name: 'Verse',  bars: 4, density: d('switch') },
      { name: 'Hook',   bars: 8, density: d('main') },
      { name: 'Outro',  bars: 4, density: d('out') },
    ],
  },
  {
    id: 'latin-heat',
    name: 'Latin Heat',
    totalBars: 68,
    teachingConcept: 'Rhythmic repetition with variation',
    tooltip: 'Latin grooves build on repetition. Each pass through the groove adds subtle variation.',
    sections: [
      { name: 'Intro',  bars: 8,  density: d('intro') },
      { name: 'Verse',  bars: 16, density: d('groove') },
      { name: 'Bridge', bars: 4,  density: d('break_') },
      { name: 'Hook',   bars: 8,  density: d('hook') },
      { name: 'Verse',  bars: 16, density: d('groove') },
      { name: 'Hook',   bars: 8,  density: d('hook') },
      { name: 'Outro',  bars: 8,  density: d('outro') },
    ],
  },
  {
    id: 'pop-structure',
    name: 'Pop Structure',
    totalBars: 72,
    teachingConcept: 'Full commercial song form',
    tooltip: 'Hooks hit hardest when everything plays at once. This is where all your tracks align.',
    sections: [
      { name: 'Intro',  bars: 4,  density: d('intro') },
      { name: 'Verse',  bars: 16, density: d('verse') },
      { name: 'Pre-Hook', bars: 4,  density: d('pre') },
      { name: 'Hook',   bars: 8,  density: d('chorus') },
      { name: 'Verse',  bars: 12, density: d('verse') },
      { name: 'Pre-Hook', bars: 4,  density: d('pre') },
      { name: 'Hook',   bars: 8,  density: d('chorus') },
      { name: 'Bridge', bars: 4,  density: d('bridge'), noDrums: true },
      { name: 'Hook',   bars: 8,  density: d('chorus') },
      { name: 'Outro',  bars: 4,  density: d('outro') },
    ],
  },
  {
    id: 'drill-pressure',
    name: 'Drill Pressure',
    totalBars: 40,
    teachingConcept: 'Intensity and relentless energy',
    tooltip: 'Drill stays intense. Short switches give the ear a moment before the pressure returns.',
    sections: [
      { name: 'Intro',  bars: 2,  density: d('intro') },
      { name: 'Verse',  bars: 12, density: d('main') },
      { name: 'Bridge', bars: 4,  density: d('switch') },
      { name: 'Verse',  bars: 8,  density: d('main') },
      { name: 'Hook',   bars: 4,  density: d('drop') },
      { name: 'Verse',  bars: 8,  density: d('main') },
      { name: 'Outro',  bars: 2,  density: d('out') },
    ],
  },
  {
    id: 'freestyle-flow',
    name: 'Freestyle Flow',
    totalBars: 44,
    teachingConcept: 'Free-form expression with arc',
    tooltip: 'Bridges change the energy. Drop the drums, keep the melody. Let the listener breathe.',
    sections: [
      { name: 'Intro', bars: 8, density: d('open') },
      { name: 'Verse', bars: 8, density: d('build') },
      { name: 'Hook',  bars: 8, density: d('peak') },
      { name: 'Verse', bars: 8, density: d('build') },
      { name: 'Hook',  bars: 8, density: d('peak') },
      { name: 'Outro', bars: 4, density: d('fade') },
    ],
  },
];

// Section colors for markers
export const SECTION_COLORS = {
  Intro: '#00D4AA', Outro: '#00D4AA',
  Verse: '#4DA6FF',
  Hook: '#FF2D78',
  'Pre-Hook': '#FF8A00',
  Bridge: '#B44DFF',
};

// =====================================================================
// 8.2 Distribution Algorithm — 7 steps
// =====================================================================

/**
 * Run the auto arranger with a selected template.
 *
 * @param {string} templateId - One of the TEMPLATES[].id values
 * @param {Array} tracks - Current tracks with clips
 * @returns {{ tracks: Array, sectionMarkers: Array, totalBars: number }}
 */
export function runAutoArrange(templateId, tracks, sessionBpm) {
  const template = TEMPLATES.find((t) => t.id === templateId);
  if (!template) throw new Error(`Unknown template: ${templateId}`);

  // Explicit MAX_BARS guard: no template can exceed the system limit
  const effectiveTotalBars = Math.min(template.totalBars, BARS_RANGE.max);

  // Step 1: Group available loops by instrument type
  const byInstrument = {};
  for (const track of tracks) {
    const inst = track.instrument;
    if (!byInstrument[inst]) byInstrument[inst] = [];
    byInstrument[inst].push(track);
  }

  // Step 2: Build section timeline with densities
  let currentBar = 0;
  const sectionTimeline = template.sections.map((sec) => {
    const entry = { ...sec, startBar: currentBar };
    currentBar += sec.bars;
    return entry;
  });

  // Batch 3.5: force at least one (or two, across distinct instrument groups)
  // track to place a real clip in the very first section, so an arrangement
  // can never roll an empty/silent intro. Every other section and track is
  // unaffected, still governed by the existing density/randomness below.
  const forcedIntroTrackIds = new Set(pickForcedIntroTracks(tracks).map((t) => t.id));
  const firstSection = sectionTimeline[0];

  // Step 3-6: For each track, iterate sections and distribute clips
  const newTracks = tracks.map((track) => {
    const newClips = [];

    for (const section of sectionTimeline) {
      // Step 6: Bridge sections — remove drums, keep melody/texture
      // Explicit flag, not the section name: names were standardized (July: Intro,
      // Verse, Pre-Hook, Hook, Bridge, Outro) and only the sections that already
      // dropped drums (Pop's Bridge, Lo-Fi's old "Drift") should keep doing so.
      if (section.noDrums && track.instrument === 'drums') continue;

      const isForcedIntroTrack = section === firstSection && forcedIntroTrackIds.has(track.id);

      // Step 6: Intros — sparse, 1-3 tracks only (density handles this)
      // Step 3: If Math.random() < section.density, place clips
      if (!isForcedIntroTrack && Math.random() > section.density) continue;

      // Step 4: Fill section with loop repetitions. Lengths are in session bars: a
      // 60 BPM loop in a 120 BPM beat spans twice its native bars on the timeline.
      const sourceClip = track.clips[0];
      if (!sourceClip) continue;
      const loopBars = sessionBpm ? timelineBars(sourceClip, sessionBpm) : getRepresentativeLoopBars(track);
      if (loopBars <= 0) continue;

      const repsNeeded = Math.ceil(section.bars / loopBars);
      let barCursor = section.startBar;

      for (let r = 0; r < repsNeeded; r++) {
        const clipBars = Math.min(loopBars, section.startBar + section.bars - barCursor);
        if (clipBars <= 0) break;

        // Step 5: Add variation — randomly skip some repetitions at lower densities.
        // A forced intro track's very first repetition (r === 0) is exempt, that
        // is the one guaranteed clip the bar-1 guarantee actually depends on.
        const isGuaranteedFirstRep = isForcedIntroTrack && r === 0;
        if (!isGuaranteedFirstRep && section.density < 0.8 && Math.random() > (section.density + 0.3)) {
          barCursor += clipBars;
          continue;
        }

        // Copy the source clip so nothing is lost: its tempo (sourceBpm), audio link,
        // style, key and every Knock Engine field. Rebuilding from loop_id alone made
        // half/double-time loops play cut short and Knock basslines silent after a
        // reload. `bars` stays in the clip's native bars.
        const isFullRep = clipBars >= loopBars;
        newClips.push({
          ...sourceClip,
          id: `clip-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          name: sourceClip.name || track.name,
          color: track.classSlot
            ? (sourceClip.color || track.color)
            : (INSTRUMENT_COLORS[track.instrument] || sourceClip.color || track.color),
          start_bar: snapToGrid(barCursor),
          bars: sessionBpm ? sessionBarsToNative(clipBars, sourceClip, sessionBpm) : clipBars,
          isWholeLoop: isFullRep && sourceClip.isWholeLoop !== false,
          fade_in: 0,
          fade_out: 0,
        });

        barCursor += clipBars;
      }
    }

    return { ...track, clips: newClips };
  });

  // Step 7: Generate section markers at each template boundary (within effective limit)
  const sectionMarkers = sectionTimeline
    .filter((sec) => sec.startBar < effectiveTotalBars)
    .map((sec) => ({
      name: sec.name,
      startBar: sec.startBar,
      color: SECTION_COLORS[sec.name] || '#00D4AA',
    }));

  return {
    tracks: newTracks,
    sectionMarkers,
    totalBars: effectiveTotalBars,
  };
}

/** Get the bar length of the first clip, or default 2. */
function getRepresentativeLoopBars(track) {
  if (track.clips.length > 0) return track.clips[0].bars;
  return 2;
}

/** Get the loop_id from the first clip. */
function getRepresentativeLoopId(track) {
  if (track.clips.length > 0) return track.clips[0].loop_id;
  return 'unknown';
}
